import {
    Directive,
    ElementRef,
    HostBinding,
    HostListener,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Renderer2
} from '@angular/core';
import FocusOutEvent = JQuery.FocusOutEvent;

@Directive({
    selector: '[bookviewerAccessbilityComponent]',
    standalone: true
})
export class AccessibilityComponentDirective implements OnInit, OnDestroy {
    @Input() badgeInstruction = 'Drücken Sie Enter, um zu interagieren';

    private elementRef = inject(ElementRef);
    private renderer = inject(Renderer2);

    private isEntered = false;
    private originalTabIndices = new Map<HTMLElement, string | null>();
    private badgeElement: HTMLElement | null = null;
    private observer: MutationObserver | null = null;
    private isUsingMouse = false;

    @HostBinding('attr.aria-expanded')
    get ariaExpanded(): string {
        return this.isEntered ? 'true' : 'false';
    }

    ngOnInit(): void {
        this.renderer.setAttribute(this.elementRef.nativeElement, 'tabindex', '-1');

        // Initial setup
        this.setChildrenFocusable(false);

        // Observe DOM changes to catch dynamically rendered elements
        if (typeof MutationObserver !== 'undefined') {
            this.observer= new MutationObserver(() => {
                this.observer?.disconnect();
                this.setChildrenFocusable(this.isEntered);
                this.reconnectObserver();
            });
            this.reconnectObserver();
        }
    }

    ngOnDestroy(): void {
        if (this.observer) {
            this.observer.disconnect();
        }
        this.hideBadge();
    }

    private reconnectObserver(): void {
        if (this.observer) {
            this.observer.observe(this.elementRef.nativeElement, {
                childList: true,
                subtree: true
            });
        }
    }

    @HostListener('keydown.enter', ['$event'])
    @HostListener('keydown.space', ['$event'])
    onEnter(event: KeyboardEvent): void {
        if (!this.isEntered) {
            const focusables = this.getFocusableChildren();
            if (focusables.length > 0) {
                event.preventDefault();
                this.enterLevel2()
            }
        }
    }

    @HostListener('keydown.escape', ['$event'])
    onEscape(event: KeyboardEvent): void {
        if (this.isEntered) {
            event.preventDefault();
            this.exitLevel2();
        } else {
            event.preventDefault();
            const activeElement = document.activeElement as HTMLElement;
            if (activeElement) {
                activeElement.blur();
            }
        }
    }

    @HostListener('focus', ['$event'])
    onFocus(event: FocusEvent): void {
        if (this.isUsingMouse) {
            return;
        }

        this.isEntered = false;

        this.showBadge();
    }

    @HostListener('blur', ['$event'])
    onBlur(event: FocusEvent): void {
        this.hideBadge();
    }

    @HostListener('focusout', ['$event'])
    onFocusOut(event: FocusOutEvent): void {
        if (this.isUsingMouse) {
            return;
        }

        const relatedTarget = event.relatedTarget as HTMLElement;
        if (this.isEntered && relatedTarget && !this.elementRef.nativeElement.contains(relatedTarget)) {
            this.exitLevel2(false);
        }
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        this.isUsingMouse = false;

        if (!this.isEntered) {
            return;
        }

        if (event.key === 'Tab') {
            const focusables = this.getFocusableChildren();
            if (focusables.length === 0) {
                return;
            }

            const firstElement = focusables[0];
            const lastElement = focusables[focusables.length - 1];
            const activeElement = document.activeElement as HTMLElement;

            if (event.shiftKey) {
                // Shift + Tab: if on first element, wrap to last
                if (activeElement === firstElement || !this.elementRef.nativeElement.contains(activeElement)) {
                    event.preventDefault();
                    this.focusElement(lastElement);
                }
            } else {
                // Tab: if on last element, wrap to first
                if (activeElement === lastElement) {
                    event.preventDefault();
                    this.focusElement(firstElement);
                }
            }
        }
    }

    private focusElement(el: HTMLElement): void {
        el.focus();
        this.renderer.addClass(el, 'a11y-focused');
        const removeListener = this.renderer.listen(el, 'blur', () => {
            this.renderer.removeClass(el, 'a11y-focused');
            removeListener();
        });
    }

    private enterLevel2(targetElement?: HTMLElement): void {
        this.isEntered = true;
        this.hideBadge()
        this.renderer.setAttribute(this.elementRef.nativeElement, 'tabindex', '-1');
        this.setChildrenFocusable(true);

        if (targetElement) {
            // Focus a specific element inside the component if targetElement is specified
            this.focusElement(targetElement);
        } else {
            // Focus the first interactive element
            const focusables = this.getFocusableChildren();
            if (focusables.length > 0) {
                const visibleTarget = this.getFirstVisibleFocusableChild(focusables);

                if (visibleTarget) {
                    this.focusElement(visibleTarget);

                    return;
                }

                this.focusElement(focusables[0]);
            }
        }
    }

    private exitLevel2(shouldFocusHost = true): void {
        this.isEntered = false;
        this.setChildrenFocusable(false);
        // Only set tabindex to 0 if there are actually focusable elements
        const focusables = this.getFocusableChildren();
        if (focusables.length > 0) {
            this.renderer.setAttribute(this.elementRef.nativeElement, 'tabindex', '0');
        } else {
            this.renderer.setAttribute(this.elementRef.nativeElement, 'tabindex', '-1');
        }
        if (shouldFocusHost) {
            this.focusElement(this.elementRef.nativeElement);
        } else {
            this.renderer.removeClass(this.elementRef.nativeElement, 'a11y-focused');
        }
    }

    private getFocusableChildren(): Array<HTMLElement> {
        const elements = this.elementRef.nativeElement.querySelectorAll(
            'a, button, input, select, textarea, [tabindex], iframe'
        );

        const focusables: Array<HTMLElement> = [];
        elements.forEach((el: HTMLElement) => {
            if (el.closest('.modal') || el.closest('.modal-dialog')) {
                return;
            }

            if (el === this.badgeElement) {
                return;
            }
            // Filter out elements that are hidden or disabled or in skip-links
            const isVisible = el.offsetWidth > 0 && el.offsetHeight > 0;
            const isNotDisabled = !(el as any).disabled;
            if (isVisible && isNotDisabled && !this.isElementSkipped(el)) {
                focusables.push(el);
            }
        });

        return focusables;
    }

    private setChildrenFocusable(focusable: boolean): void {
        const elements = this.elementRef.nativeElement.querySelectorAll(
            'a, button, input, select, textarea, [tabindex], iframe'
        );

        let hasFocusables = false;

        elements.forEach((el: HTMLElement) => {
            if (el.closest('.modal') || el.closest('.modal-dialog')) {
                return;
            }

            if (el === this.badgeElement) {
                return;
            }
            if (this.isElementSkipped(el)) {
                return;
            }

            hasFocusables = true;

            if (!focusable) {
                if (!this.originalTabIndices.has(el)) {
                    const originalAttr = el.getAttribute('tabindex');
                    this.originalTabIndices.set(el, originalAttr);
                }
                this.renderer.setAttribute(el, 'tabindex', '-1');
            } else {
                if (this.originalTabIndices.has(el)) {
                    const originalAttr = this.originalTabIndices.get(el);
                    if (originalAttr !== null && originalAttr !== undefined) {
                        this.renderer.setAttribute(el, 'tabindex', originalAttr);
                    } else {
                        const nativeTags = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'IFRAME'];
                        if (nativeTags.includes(el.tagName)) {
                            this.renderer.removeAttribute(el, 'tabindex');
                        } else {
                            this.renderer.setAttribute(el, 'tabindex', '0');
                        }
                    }
                }
            }

            // Set host tabindex based on focusable state
            if (!this.isEntered) {
                if (hasFocusables) {
                    this.renderer.setAttribute(this.elementRef.nativeElement, 'tabindex', '0');
                } else {
                    this.renderer.setAttribute(this.elementRef.nativeElement, 'tabindex', '-1');
                }
            }
        });
    }

    @HostListener('focusin', ['$event'])
    onFocusIn(event: FocusEvent): void {
        if (this.isUsingMouse) {
            return;
        }

        const target = event.target as HTMLElement;

        if (!this.isEntered && target && target !== this.elementRef.nativeElement) {
            this.enterLevel2(target);
        }
    }

    private showBadge(): void {
        if (this.badgeElement) {
            return;
        }

        const focusables = this.getFocusableChildren();
        if (focusables.length === 0) {
            return;
        }

        this.badgeElement = this.renderer.createElement('div');
        this.renderer.addClass(this.badgeElement, 'bookviewer-a11y-badge');
        this.renderer.setAttribute(this.badgeElement, 'aria-live', 'polite');

        const text = this.renderer.createText(this.badgeInstruction);
        this.renderer.appendChild(this.badgeElement, text);
        this.renderer.appendChild(document.body, this.badgeElement);
    }

    private hideBadge(): void {
        if (this.badgeElement) {
            this.renderer.removeChild(document.body, this.badgeElement);
            this.badgeElement = null;
        }
    }

    private getFirstVisibleFocusableChild(focusables: Array<HTMLElement>): HTMLElement | null {
        for (const el of focusables) {
            const rect = el.getBoundingClientRect();
            if (rect.top >= 0 && rect.top <= (window.innerHeight || document.documentElement.clientHeight)) {
                return el;
            }
        }

        return null;
    }

    private isElementSkipped(el: HTMLElement): boolean {
        const isSkipLink = el.classList.contains('skip-link') || el.closest('.bookviewer-skip-links');
        const skipExplicitly = el.hasAttribute('data-skip-a11y');

        return Boolean(isSkipLink || skipExplicitly);
    }

    @HostListener('mousedown')
    onMouseDown(): void {
        this.isUsingMouse = true;
    }
}
